import { useState } from 'react';
import { C, FONTS } from '../../tokens/design';
import { useMspTeam } from '../../hooks/useMspData';
import { useApiClient } from '../../api/apiClient';
import Shell from '../../components/layout/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import Skeleton from '../../components/ui/Skeleton';
import { TH, TR, TD } from '../../components/ui/Table';
import PageError from '../../components/ui/PageError';
import { relativeTime, exactTime } from '../../utils/time';

const ROLES = ['Admin', 'Analyst', 'Viewer'];

const roleBadge = (role) => {
  const colors = { Admin: C.pur, Analyst: C.sky, Viewer: C.t3 };
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      fontFamily: FONTS.mono, color: colors[role] || C.t2,
      background: `${colors[role] || C.t3}14`,
      border: `1px solid ${colors[role] || C.t3}33`,
    }}>{role}</span>
  );
};

const statusDot = (status) => {
  const active = status === 'Active' || status === 'active';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: active ? C.grn : C.t3 }} />
      <span style={{ fontSize: 10, color: active ? C.grn : C.t3, fontFamily: FONTS.mono }}>{active ? 'Active' : 'Inactive'}</span>
    </div>
  );
};

function InviteModal({ onClose, api, onSuccess }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Analyst');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleInvite = async () => {
    if (!email) return;
    setSending(true);
    try {
      await api.post('/team/invite', { email, role });
      setResult({ ok: true, msg: `Invitation sent to ${email}` });
      onSuccess();
    } catch (err) {
      setResult({ ok: false, msg: err.message || 'Failed to send invite' });
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
    padding: '8px 10px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 28, maxWidth: 440, width: '90%' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>Invite Team Member</div>
        <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 18 }}>Send an invitation to join your MSP portal.</div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Email Address</div>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="user@company.com" style={inputStyle} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Role</div>
          <select value={role} onChange={e => setRole(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {result && (
          <div style={{
            marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono,
            background: result.ok ? `${C.grn}12` : C.redBg,
            border: `1px solid ${result.ok ? C.grn : C.red}33`,
            color: result.ok ? C.grn : C.red,
          }}>{result.msg}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="sec" onClick={onClose}>{result?.ok ? 'Close' : 'Cancel'}</Button>
          {!result?.ok && (
            <Button variant="pri" onClick={handleInvite} disabled={sending || !email}>
              {sending ? 'Sending...' : 'Send Invite'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function RemoveConfirmModal({ member, onClose, api, onSuccess }) {
  const [removing, setRemoving] = useState(false);
  const name = member.name || member.email || 'this user';
  const id = member.memberId || member.userId || member.id;

  const [removeError, setRemoveError] = useState(null);

  const handleRemove = async () => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await api.delete(`/team/${id}`);
      onSuccess();
      onClose();
    } catch (err) {
      setRemoveError(err.message);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 28, maxWidth: 420, width: '90%' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 10 }}>Remove Team Member</div>
        <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono, marginBottom: 20, lineHeight: 1.6 }}>
          Are you sure you want to remove <strong style={{ color: C.t1 }}>{name}</strong> from the team? They will lose access to the MSP portal immediately.
        </div>
        {removeError && (
          <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red }}>{removeError}</div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="sec" onClick={onClose}>Cancel</Button>
          <Button variant="pri" onClick={handleRemove} disabled={removing} style={{ background: C.red }}>
            {removing ? 'Removing...' : 'Remove Member'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Team() {
  const api = useApiClient();
  const { data: members, loading, error, refetch } = useMspTeam();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeMember, setRemoveMember] = useState(null);
  const [changingRole, setChangingRole] = useState(null);
  const [roleError, setRoleError] = useState(null);

  const handleRoleChange = async (member, newRole) => {
    const id = member.memberId || member.userId || member.id;
    setChangingRole(id);
    setRoleError(null);
    try {
      await api.put(`/team/${id}/role`, { role: newRole });
      refetch();
    } catch (err) {
      setRoleError(err.message);
    } finally {
      setChangingRole(null);
    }
  };

  return (
    <Shell title="Team Members" sub={`${members.length} members`}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, justifyContent: 'flex-end' }}>
        <Button variant="pri" onClick={() => setInviteOpen(true)}>+ Invite Member</Button>
      </div>

      {roleError && (
        <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Failed to change role: {roleError}</span>
          <button onClick={() => setRoleError(null)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 14 }}>{'\u2715'}</button>
        </div>
      )}

      {loading && (
        <Card>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <TH cols={['Name', 'Email', 'Role', 'Last Login', 'Status', '']} />
            <tbody>
              {[0, 1, 2].map(i => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.b2}` }}>
                  <TD><Skeleton width={120} height={12} /></TD>
                  <TD><Skeleton width={160} height={12} /></TD>
                  <TD><Skeleton width={60} height={18} /></TD>
                  <TD><Skeleton width={80} height={12} /></TD>
                  <TD><Skeleton width={55} height={12} /></TD>
                  <TD><Skeleton width={100} height={24} /></TD>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {error && <PageError error={error} onRetry={refetch} />}

      {!loading && !error && members.length === 0 && (
        <Card style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans, marginBottom: 8 }}>No team members yet</div>
          <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 16 }}>Invite your first team member to get started.</div>
          <Button variant="pri" onClick={() => setInviteOpen(true)}>+ Invite Member</Button>
        </Card>
      )}

      {!loading && !error && members.length > 0 && (
        <Card>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <TH cols={['Name', 'Email', 'Role', 'Last Login', 'Status', '']} />
            <tbody>{members.map(m => {
              const id = m.memberId || m.userId || m.id || '';
              const name = m.name || m.displayName || '';
              const email = m.email || '';
              const ROLE_MAP = { 0: 'Admin', 1: 'Analyst' };
              const role = typeof m.role === 'number' ? (ROLE_MAP[m.role] ?? 'Viewer') : (m.role || 'Viewer');
              const lastLogin = m.lastLogin || m.lastLoginAt || null;
              const status = m.status || 'Active';
              return (
                <TR key={id}>
                  <TD>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'linear-gradient(135deg,#334155,#1e293b)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0,
                      }}>{name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?'}</div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{name || '\u2014'}</span>
                    </div>
                  </TD>
                  <TD style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>{email}</TD>
                  <TD>{roleBadge(role)}</TD>
                  <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }} title={lastLogin ? exactTime(lastLogin) : ''}>
                    {lastLogin ? relativeTime(lastLogin) : '\u2014'}
                  </TD>
                  <TD>{statusDot(status)}</TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                      <select
                        value={role}
                        onChange={e => handleRoleChange(m, e.target.value)}
                        disabled={changingRole === id}
                        style={{
                          background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 4,
                          padding: '3px 6px', color: C.t1, fontSize: 10, fontFamily: FONTS.mono,
                          cursor: 'pointer', outline: 'none',
                        }}
                      >
                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <Button variant="ghost" onClick={() => setRemoveMember(m)} style={{ color: C.red, fontSize: 11, padding: '4px 8px' }}>{'\u2715'}</Button>
                    </div>
                  </TD>
                </TR>
              );
            })}</tbody>
          </table>
        </Card>
      )}

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} api={api} onSuccess={() => refetch()} />}
      {removeMember && <RemoveConfirmModal member={removeMember} onClose={() => setRemoveMember(null)} api={api} onSuccess={() => refetch()} />}
    </Shell>
  );
}
